import { z } from 'zod';

import { NotificationKind } from '../enums';
import { AvailabilitySchema, IdSchema } from './common';
import { UserLevelInputSchema } from './onboarding';

/**
 * Минимальный профиль (BLUEPRINT Stage 3, задача 5): районы, вечера, уровни.
 * Пока пользователь в пуле, правка меняет и его заявки (`PoolApplication`).
 */
export const ProfileUpdateInputSchema = z
  .object({
    districtIds: z.array(IdSchema).min(1, 'Выбери хотя бы один район').max(5).optional(),
    availability: AvailabilitySchema.optional(),
    levels: z
      .array(UserLevelInputSchema)
      .max(12)
      .refine((arr) => new Set(arr.map((l) => l.activityId)).size === arr.length, {
        message: 'Уровень по каждой активности указывается один раз',
      })
      .optional(),
  })
  .refine((v) => v.districtIds || v.availability || v.levels, {
    message: 'Нечего сохранять',
  });
export type ProfileUpdateInput = z.infer<typeof ProfileUpdateInputSchema>;

/**
 * Уведомления (`User.notifyPrefs`, Stage 6 задача 6): переключатели по циклу сбора (3.2, шаг 6).
 * Применяются планировщиком Stage 8; сообщения о наборе, продажах и дедлайне оплаты не отключаются.
 */
export const NOTIFY_PREF_KEYS = ['rsvpRequest', 'details24h', 'details3h', 'feedback'] as const;
export type NotifyPrefKey = (typeof NOTIFY_PREF_KEYS)[number];

/** Переключатели, которыми управляет участник на `/profile`. */
export const NotifyTogglesSchema = z.object({
  /** «Идёшь?» за 48 часов */
  rsvpRequest: z.boolean(),
  /** Детали и состав за 24 часа */
  details24h: z.boolean(),
  /** Адрес и что взять за 3 часа */
  details3h: z.boolean(),
  /** Тихая обратная связь после сбора */
  feedback: z.boolean(),
});
export type NotifyToggles = z.infer<typeof NotifyTogglesSchema>;

/**
 * Полные `User.notifyPrefs`: переключатели участника плюс служебный `blocked` —
 * его ставит `notify.service`, когда Telegram отвечает 403 (пользователь заблокировал бота).
 */
export const NotifyPrefsSchema = NotifyTogglesSchema.extend({
  blocked: z.boolean(),
});
export type NotifyPrefs = z.infer<typeof NotifyPrefsSchema>;

export const NOTIFY_PREF_DEFAULTS: NotifyPrefs = {
  rsvpRequest: true,
  details24h: true,
  details3h: true,
  feedback: true,
  blocked: false,
};

/** `User.notifyPrefs` (JSON) → полный объект с дефолтами; мусор игнорируется. */
export function parseNotifyPrefs(raw: unknown): NotifyPrefs {
  const partial = NotifyPrefsSchema.partial().safeParse(raw ?? {});
  return { ...NOTIFY_PREF_DEFAULTS, ...(partial.success ? partial.data : {}) };
}

/** Наружу (Mini App) уходят только переключатели: `blocked` — служебный флаг. */
export function notifyToggles(prefs: NotifyPrefs): NotifyToggles {
  return {
    rsvpRequest: prefs.rsvpRequest,
    details24h: prefs.details24h,
    details3h: prefs.details3h,
    feedback: prefs.feedback,
  };
}

export const ProfileNotifyPrefsInputSchema = NotifyTogglesSchema.partial().refine(
  (v) => Object.keys(v).length > 0,
  { message: 'Нечего сохранять' },
);
export type ProfileNotifyPrefsInput = z.infer<typeof ProfileNotifyPrefsInputSchema>;

/**
 * Какие виды уведомлений выключаются переключателем (Stage 8, задача 2).
 * Набор состава, продажи, дедлайн оплаты и ручные сообщения ops не отключаются.
 */
export const NOTIFY_PREF_BY_KIND: Readonly<Partial<Record<NotificationKind, NotifyPrefKey>>> = {
  [NotificationKind.RSVP_REQUEST]: 'rsvpRequest',
  [NotificationKind.DETAILS_24H]: 'details24h',
  [NotificationKind.DETAILS_3H]: 'details3h',
  [NotificationKind.FEEDBACK]: 'feedback',
};

/** Согласен ли участник получать уведомление этого вида. */
export function isNotifyAllowed(kind: NotificationKind, prefs: NotifyPrefs): boolean {
  const key = NOTIFY_PREF_BY_KIND[kind];
  return key ? prefs[key] : true;
}
